/**
 * Authentication Store
 */
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { AuthStore } from './types/auth';
import { AuthState, LoginRequest, LoginResponse, LoginResult } from '../types/auth';
import { authService } from '../services/authService';
import { withErrorHandling } from './utils/storeHelpers';

const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        user: null,
        isAuthenticated: false,
        token: null,
        tenantId: null,
        loginAttempts: 0,
        maxLoginAttempts: MAX_LOGIN_ATTEMPTS,
        isLocked: false,
        lockoutExpiresAt: null,
        isLoading: false,
        error: null,
        lastUpdated: null,

        // Authentication actions
        login: async (credentials: LoginRequest): Promise<LoginResponse> => {
          const { setLoading, setError, incrementLoginAttempts, lockAccount } = get();

          // Check if account is locked
          if (get().isLocked) {
            const timeLeft = get().getTimeUntilUnlock();
            if (timeLeft > 0) {
              const minutes = Math.ceil(timeLeft / 60000);
              const errorMessage = `Account is locked. Try again in ${minutes} minute(s).`;
              setError(errorMessage);
              return {
                token: null,
                tenantId: null,
                errorText: errorMessage
              };
            } else {
              // Unlock account if lockout period has expired
              get().unlockAccount();
            }
          }

          const result = await withErrorHandling(
            async () => {
              // authService.login returns LoginResult { success, error }
              const loginResult: LoginResult = await authService.login(credentials.userName, credentials.password);

              if (loginResult.success) {
                // Login successful - get token from storage
                const token = authService.getToken();
                const tenantId = authService.getTenantId();

                const user: AuthState = {
                  token,
                  tenantId,
                  isAuthenticated: true
                };

                set((state) => {
                  state.user = user;
                  state.isAuthenticated = true;
                  state.token = token;
                  state.tenantId = tenantId;
                  state.loginAttempts = 0;
                  state.isLocked = false;
                  state.lockoutExpiresAt = null;
                  state.lastUpdated = Date.now();
                });

                // Note: authService.storeTokens() already stored token/tenant to localStorage
                // Don't use setInStorage here as it JSON-stringifies, conflicting with authService's raw strings

                get().resetLoginAttempts();

                return {
                  token,
                  tenantId,
                  errorText: null
                };
              } else {
                // Login failed
                incrementLoginAttempts();

                if (get().loginAttempts >= MAX_LOGIN_ATTEMPTS) {
                  lockAccount();
                }

                return {
                  token: null,
                  tenantId: null,
                  errorText: loginResult.error || 'Login failed'
                };
              }
            },
            setError,
            setLoading
          );

          return result || {
            token: null,
            tenantId: null,
            errorText: 'Login failed'
          };
        },

        logout: () => {
          set((state) => {
            state.user = null;
            state.isAuthenticated = false;
            state.token = null;
            state.tenantId = null;
            state.loginAttempts = 0;
            state.isLocked = false;
            state.lockoutExpiresAt = null;
            state.error = null;
            state.lastUpdated = Date.now();
          });

          // authService.logout() clears localStorage and reloads the page
          authService.logout();
        },

        refreshToken: async () => {
          // TODO: Implement token refresh logic when backend supports it
        },

        // User state management
        setUser: (user: AuthState | null) => {
          set((state) => {
            state.user = user;
            state.isAuthenticated = !!user?.token;
            state.token = user?.token || null;
            state.tenantId = user?.tenantId || null;
            state.lastUpdated = Date.now();
          });

          // Note: Don't use setInStorage for token/tenant as it JSON-stringifies
          // authService uses raw strings in localStorage, which would conflict
          // Zustand persist middleware handles state persistence to 'auth-store' key
        },

        setToken: (token: string | null) => {
          set((state) => {
            state.token = token;
            state.isAuthenticated = !!token;
            if (state.user) {
              state.user.token = token;
              state.user.isAuthenticated = !!token;
            }
            state.lastUpdated = Date.now();
          });
          // Token storage handled by authService (uses raw strings, not JSON)
        },

        setTenantId: (tenantId: string | null) => {
          set((state) => {
            state.tenantId = tenantId;
            if (state.user) {
              state.user.tenantId = tenantId;
            }
            state.lastUpdated = Date.now();
          });
          // Tenant storage handled by authService (uses raw strings, not JSON)
        },

        // Account security
        incrementLoginAttempts: () => {
          set((state) => {
            state.loginAttempts += 1;
          });
        },

        resetLoginAttempts: () => {
          set((state) => {
            state.loginAttempts = 0;
          });
        },

        lockAccount: () => {
          set((state) => {
            state.isLocked = true;
            state.lockoutExpiresAt = Date.now() + LOCKOUT_DURATION;
            state.error = `Account locked due to too many failed login attempts. Try again in ${LOCKOUT_DURATION / 60000} minutes.`;
          });
        },

        unlockAccount: () => {
          set((state) => {
            state.isLocked = false;
            state.lockoutExpiresAt = null;
            state.loginAttempts = 0;
            state.error = null;
          });
        },

        // Utility methods
        getAuthHeaders: () => {
          const { token, tenantId } = get();
          const headers: Record<string, string> = {};

          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }

          if (tenantId) {
            headers['X-Tenant-ID'] = tenantId;
          }

          return headers;
        },

        isTokenExpired: () => {
          // TODO: Implement token expiration check when backend supports it
          const { token } = get();
          if (!token) return true;
          
          // For now, just check if token exists
          return false;
        },

        getTimeUntilUnlock: () => {
          const { lockoutExpiresAt } = get();
          if (!lockoutExpiresAt) return 0;
          
          const now = Date.now();
          const timeLeft = lockoutExpiresAt - now;
          return Math.max(0, timeLeft);
        },

        // Common actions
        setLoading: (loading: boolean) => {
          set((state) => {
            state.isLoading = loading;
            if (loading) {
              state.error = null;
            }
          });
        },

        setError: (error: string | null) => {
          set((state) => {
            state.error = error;
            state.isLoading = false;
          });
        },

        clearError: () => {
          set((state) => {
            state.error = null;
          });
        },

        reset: () => {
          set((state) => {
            state.user = null;
            state.isAuthenticated = false;
            state.token = null;
            state.tenantId = null;
            state.loginAttempts = 0;
            state.isLocked = false;
            state.lockoutExpiresAt = null;
            state.isLoading = false;
            state.error = null;
            state.lastUpdated = null;
          });

          // Clear localStorage via authService for consistent storage format
          authService.clearAuth();
        },
      })),
      {
        name: 'auth-store',
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          token: state.token,
          tenantId: state.tenantId,
          loginAttempts: state.loginAttempts,
          isLocked: state.isLocked,
          lockoutExpiresAt: state.lockoutExpiresAt,
        }),
      }
    ),
    { name: 'auth-store' }
  )
);

// Initialize store from existing auth state
const initializeAuth = () => {
  const authState = authService.getAuthState();
  if (authState.isAuthenticated) {
    useAuthStore.getState().setUser(authState);
  }
};

// Initialize on store creation
initializeAuth();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).getAuthStore = () => useAuthStore.getState();
}